'use client';

import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import RevenueCatDebug from './components/RevenueCatDebug';
import RevenueCatCustomerLink from './components/RevenueCatCustomerLink';
import usePageTitle from './utils/usePageTitle';
import { getUserCountry } from './utils/countryPricing';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { showIntercomForHelp } from './utils/intercomHelpers';
import { loadStripe } from '@stripe/stripe-js';

// Pre-initialize Stripe SDK so it's cached when /checkout mounts
void loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper to format phone numbers nicely
function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  
  // For standard E.164 format like +14159998941
  if (phoneNumber.startsWith('+')) {
    // Format US numbers with parentheses
    if (phoneNumber.startsWith('+1') && phoneNumber.length === 12) {
      return `+1(${phoneNumber.substring(2, 5)})${phoneNumber.substring(5, 8)}-${phoneNumber.substring(8, 12)}`;
    }
    // Format other international numbers with parentheses around country code
    const countryCode = phoneNumber.substring(1).match(/^\d+/)[0];
    const restOfNumber = phoneNumber.substring(1 + countryCode.length);
    if (restOfNumber.length > 6) {
      return `+${countryCode}(${restOfNumber.substring(0, 3)})${restOfNumber.substring(3, 6)}-${restOfNumber.substring(6)}`;
    }
    return phoneNumber; // Fallback for unusual formats
  }
  
  return phoneNumber;
}

// Fire TriplePixel Contact event with user data
function fireTriplePixelEvent(user) {
  if (window.TriplePixel && user.email) {
    // Create a script element to execute the TriplePixel Contact event
    const script = document.createElement('script');
    script.textContent = `
      ~function TP() {
        const email = "${user.email}";
        if (!email || !window.TriplePixel) {setTimeout(TP, 400); return;}
        TriplePixel("Contact", {email: email});
      }()
    `;
    document.head.appendChild(script);
  } else if (window.TriplePixel && user.phoneNumber) {
    // If email is not available but phone is, use phone instead
    const script = document.createElement('script');
    script.textContent = `
      ~function TP() {
        const phone = "${user.phoneNumber}";
        if (!phone || !window.TriplePixel) {setTimeout(TP, 400); return;}
        TriplePixel('Contact', {phone: phone});
      }()
    `;
    document.head.appendChild(script);
  }
}

// Plan configurations
// NOTE: `weekly` is intentionally HIDDEN from the UI (see `hidden: true`). The config is
// kept so the plan can be re-enabled quickly and so the backend keeps honoring existing
// abandoned-cart links. The monthly plan is now the only visible consumer plan and uses
// the same authorization-hold + free-trial flow (3-day trial).
const PLANS = {
  weekly: {
    id: 'price_1SG8DCK5A174Olbc6IiZ6685',
    bundleId: 'Bundle1LinesWeeklyMsg_NEW',
    name: 'Weekly Plan',
    price: '$7.99',
    period: 'Weekly',
    displayPeriod: '/week',
    hasTrial: true,
    trialDays: 7,
    isMessaging: true,
    hidden: true,
    subtitle: 'Most popular choice',
    features: [
      'Email relay protection',
      'Phone number privacy', 
      'Call & SMS forwarding',
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '🇺🇸 🇨🇦 & 🇬🇧 numbers'
    ]
  },
  monthly: {
    id: 'price_1SUHVAK5A174OlbcGyZSQjoQ',
    bundleId: 'Bundle1LinesMonthlyMsg_EXP',
    name: 'Monthly Plan',
    price: '$19.99',
    period: 'Month',
    displayPeriod: '/month',
    hasTrial: true,
    trialDays: 3,
    isMessaging: true,
    subtitle: 'Most popular choice',
    features: [
      'Email relay protection',
      'Phone number privacy',
      'Call & SMS forwarding', 
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '🇺🇸 🇨🇦 & 🇬🇧 numbers'
    ]
  }
};

// Default consumer plan key (the visible, primary plan on /welcome)
const DEFAULT_CONSUMER_PLAN = 'monthly';

// Business messaging plan configurations for biz landing pages
const BIZ_PLANS = {
  threeMonths: {
    id: 'price_1TS74XK5A174OlbcDNGc0V1z',
    bundleId: 'Bundle1LinesCustomMsgBiz_3months',
    name: '3-Month Plan',
    price: '$182.00',
    period: '3-months',
    displayPeriod: '/3 months',
    hasTrial: true,
    trialDays: 6,
    isMessaging: true,
    subtitle: 'Best value',
    features: [
      'Email relay protection',
      'Phone number privacy',
      'Call & SMS forwarding',
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '🇺🇸 🇨🇦 & 🇬🇧 numbers'
    ]
  },
  weekly: {
    id: 'price_1TJjeMK5A174Olbcuh7Ytct7',
    bundleId: 'Bundle1LinesWeeklyMsgBiz',
    name: 'Weekly Plan',
    price: '$29.99',
    period: 'Weekly',
    displayPeriod: '/week',
    hasTrial: false,
    isMessaging: true,
    subtitle: 'Full-featured weekly',
    features: [
      'Email relay protection',
      'Phone number privacy',
      'Call & SMS forwarding',
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '🇺🇸 🇨🇦 & 🇬🇧 numbers'
    ]
  }
};

// Toll-free plan configurations for get-1-800-toll-free-number-for-business landing
const TOLL_FREE_PLANS = {
  monthly: {
    id: 'price_1S35dPK5A174Olbc7miU9uUm',
    bundleId: 'Bundle1LinesMonthMsgToll',
    name: '1 Toll Free Line + messaging',
    price: '$9.99',
    period: 'Month',
    displayPeriod: '/month',
    hasTrial: true,
    trialDays: 30,
    isMessaging: true,
    subtitle: 'Perfect for business',
    features: [
      '1 Toll Free Number or any Area Code',
      'Phone number privacy',
      'Call & SMS forwarding',
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '✨ AI Feature: Let your own personalized AI respond to calls and messages'
    ]
  },
  annual: {
    id: 'price_1S35h6K5A174Olbc38lVIXdT',
    bundleId: 'Bundle1LinesAnnualMsgToll',
    name: '1 Toll Free Line + messaging',
    price: '$7.99',
    period: 'Annual',
    displayPeriod: '/mo when paid annually',
    originalPrice: '$95.88',
    hasTrial: true,
    trialDays: 30,
    isMessaging: true,
    subtitle: 'Best value - Save 20%',
    features: [
      '1 Toll Free Number or any Area Code',
      'Phone number privacy',
      'Call & SMS forwarding',
      'Send & receive texts online',
      'Message history & search',
      'Contact management',
      '✨ AI Feature: Let your own personalized AI respond to calls and messages'
    ]
  }
};

export default function Welcome() {
  usePageTitle('Choose Your Plan');

  // Check if user landed from toll-free page, AI phone number page, or biz page
  const [isTollFreeLanding, setIsTollFreeLanding] = useState(false);
  const [isAIPhoneLanding, setIsAIPhoneLanding] = useState(false);
  const [isBizLanding, setIsBizLanding] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const testimonials = [
    { quote: "I use ChatOdyssey for managing my online business — it's a game changer.", author: "David", location: "Miami" },
    { quote: "Finally a simple way to keep my personal number private. Highly recommend.", author: "Sarah", location: "Austin" },
    { quote: "The AI assistant feature alone is worth it. Saves me hours every week.", author: "Marcus", location: "New York" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const landingUrl = getCookie('landing');
    
    if (landingUrl) {
      // Decode the URL multiple times to handle heavy URL encoding
      let decodedUrl = landingUrl;
      try {
        // Keep decoding until it stops changing (max 15 iterations for safety)
        let previousUrl = '';
        let iterations = 0;
        while (decodedUrl !== previousUrl && decodedUrl.includes('%') && iterations < 15) {
          previousUrl = decodedUrl;
          decodedUrl = decodeURIComponent(decodedUrl);
          iterations++;
          
          // Check at each iteration in case the string appears partially decoded
          if (decodedUrl.includes('get-1-800-toll-free-number-for-business')) {
            setIsTollFreeLanding(true);
            break;
          }
          if (decodedUrl.includes('ai-phone-number')) {
            setIsAIPhoneLanding(true);
            break;
          }
          if (decodedUrl.includes('business-phone')) {
            setIsBizLanding(true);
            break;
          }
        }
        
        // Final check if not already detected
        if (!isTollFreeLanding && decodedUrl.includes('get-1-800-toll-free-number-for-business')) {
          setIsTollFreeLanding(true);
        }
        if (!isAIPhoneLanding && decodedUrl.includes('ai-phone-number')) {
          setIsAIPhoneLanding(true);
        }
        if (!isBizLanding && decodedUrl.includes('business-phone')) {
          setIsBizLanding(true);
        }
      } catch (error) {
        console.error('Error decoding landing URL:', error);
        // Fallback: check the original URL too
        if (landingUrl.includes('get-1-800-toll-free-number-for-business')) {
          setIsTollFreeLanding(true);
        }
        if (landingUrl.includes('ai-phone-number')) {
          setIsAIPhoneLanding(true);
        }
        if (landingUrl.includes('business-phone')) {
          setIsBizLanding(true);
        }
      }
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(() => {
    if (typeof window !== 'undefined') {
      const bizStored = localStorage.getItem('bizToggleEnabled') === 'true';
      const stored = localStorage.getItem('selectedPlanType');
      // Never restore a hidden consumer plan (e.g. a stale 'weekly' from a previous session)
      const storedIsHiddenConsumerPlan = !bizStored && stored && PLANS[stored]?.hidden;
      if (stored && !storedIsHiddenConsumerPlan) return stored;
      return bizStored ? 'threeMonths' : DEFAULT_CONSUMER_PLAN;
    }
    return DEFAULT_CONSUMER_PLAN;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [userCountry, setUserCountry] = useState('US');
  const [countryLoaded, setCountryLoaded] = useState(false);
  const [bizToggle, setBizToggle] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('bizToggleEnabled');
      return stored === 'true';
    }
    return false;
  });

  const handleBizToggle = () => {
    const newState = !bizToggle;
    setBizToggle(newState);
    setSelectedPlan(newState ? 'threeMonths' : DEFAULT_CONSUMER_PLAN);
    if (typeof window !== 'undefined') {
      localStorage.setItem('bizToggleEnabled', newState.toString());
    }
  };

  // Reset selected plan when biz landing is detected (keys differ from regular plans)
  useEffect(() => {
    if (isBizLanding && selectedPlan !== 'threeMonths' && selectedPlan !== 'weekly') {
      setSelectedPlan('threeMonths');
    }
  }, [isBizLanding]);

  // Consumer mode guard: never leave the selection on a hidden or unknown consumer plan
  // (e.g. a stale 'weekly' key restored from localStorage, or a biz key after toggling off).
  useEffect(() => {
    const isConsumerMode = !bizToggle && !isBizLanding && !isTollFreeLanding && !isAIPhoneLanding;
    if (isConsumerMode && (!PLANS[selectedPlan] || PLANS[selectedPlan].hidden)) {
      setSelectedPlan(DEFAULT_CONSUMER_PLAN);
      if (typeof window !== 'undefined') {
        localStorage.setItem('selectedPlanType', DEFAULT_CONSUMER_PLAN);
      }
    }
  }, [bizToggle, isBizLanding, isTollFreeLanding, isAIPhoneLanding, selectedPlan]);
  
  // Reset loading state when component mounts (fixes back button issue)
  useEffect(() => {
    setIsLoading(false);
    
    // Additional reset when coming back from checkout
    const handlePageShow = () => {
      setIsLoading(false);
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setIsLoading(false);
      }
    };
    
    // Listen for page show event (fires when navigating back)
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Save selected plan to localStorage whenever it changes
  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedPlanType', plan);
    }
  };

  // Check if viewport is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // On auth state change, fetch or create aliases and get user country
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch user aliases and check for toll-free landing in stored data
        try {
          const response = await fetch('/api/createAliases', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              email: user.email || '',
              uid: user.uid, 
              attribution: {
                utm_source: getCookie('utm_source'),
                utm_medium: getCookie('utm_medium'),
                utm_campaign: getCookie('utm_campaign'),
                utm_term: getCookie('utm_term'),
                utm_content: getCookie('utm_content'),
                gclid: getCookie('gclid'),
                fbclid: getCookie('fbclid'),
                msclkid: getCookie('msclkid'),
                referrer: getCookie('referrer'),
                landing: getCookie('landing'),
                user_agent: getCookie('user_agent'),
                lang: getCookie('lang'),
                tz_offset: getCookie('tz_offset'),
                tz_name: getCookie('tz_name'),
                scr_w: getCookie('scr_w'),
                scr_h: getCookie('scr_h'),
                scr_cd: getCookie('scr_cd'),
                mem_gb: getCookie('mem_gb'),
                cpu_threads: getCookie('cpu_threads'),
                ip: getCookie('ip'),
                sid: getCookie('sid'),
              }
            }),
          });

          if (response.ok) {
            const userData = await response.json();
            
            // Fire TriplePixel event
            fireTriplePixelEvent(user);
            
            // Check stored landing URL from user data
            if (userData.landing) {
              
              // Decode the stored URL multiple times to handle heavy URL encoding
              let decodedStoredUrl = userData.landing;
              try {
                let previousUrl = '';
                let iterations = 0;
                while (decodedStoredUrl !== previousUrl && decodedStoredUrl.includes('%') && iterations < 15) {
                  previousUrl = decodedStoredUrl;
                  decodedStoredUrl = decodeURIComponent(decodedStoredUrl);
                  iterations++;
                  
                  // Check at each iteration in case the string appears partially decoded
                  if (decodedStoredUrl.includes('get-1-800-toll-free-number-for-business')) {
                    setIsTollFreeLanding(true);
                    break;
                  }
                  if (decodedStoredUrl.includes('ai-phone-number')) {
                    setIsAIPhoneLanding(true);
                    break;
                  }
                  if (decodedStoredUrl.includes('business-phone')) {
                    setIsBizLanding(true);
                    break;
                  }
                }
                
                // Final check if not already detected
                if (!isTollFreeLanding && decodedStoredUrl.includes('get-1-800-toll-free-number-for-business')) {
                  setIsTollFreeLanding(true);
                }
                if (!isAIPhoneLanding && decodedStoredUrl.includes('ai-phone-number')) {
                  setIsAIPhoneLanding(true);
                }
                if (!isBizLanding && decodedStoredUrl.includes('business-phone')) {
                  setIsBizLanding(true);
                }
              } catch (error) {
                console.error('Error decoding stored landing URL:', error);
                if (userData.landing.includes('get-1-800-toll-free-number-for-business')) {
                  setIsTollFreeLanding(true);
                }
                if (userData.landing.includes('ai-phone-number')) {
                  setIsAIPhoneLanding(true);
                }
                if (userData.landing.includes('business-phone')) {
                  setIsBizLanding(true);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user aliases:', error);
        }
        
        // Fetch user's country
        try {
          const country = await getUserCountry(user.uid);
          setUserCountry(country);
        } catch (error) {
          console.error('Error loading user country:', error);
          setUserCountry('US'); // Fallback to US
        } finally {
          setCountryLoaded(true);
        }
      } else {
        setCountryLoaded(true); // Set loaded even if no user for consistent state
      }
    });
    return unsubscribe;
  }, []);

  // Handle checkout — prefetch Stripe clientSecret before navigating
  const handleCheckout = async () => {
    const activePlans = (bizToggle || isBizLanding) ? BIZ_PLANS : (isTollFreeLanding || isAIPhoneLanding) ? TOLL_FREE_PLANS : PLANS;
    const plan = activePlans[selectedPlan];
    if (!plan) return;
    
    setIsLoading(true);
    
    const planData = {
      id: plan.id,
      bundleId: plan.bundleId,
      name: plan.name,
      price: plan.price,
      period: plan.period,
      hasTrial: plan.hasTrial,
      trialDays: plan.trialDays,
      isMessaging: plan.isMessaging,
      lines: 1,
      pricePerLine: plan.price,
      country: userCountry,
      originalPrice: plan.originalPrice || null
    };

    sessionStorage.setItem('selectedPlan', JSON.stringify(planData));

    // Prefetch: call the checkout API now so /checkout can render the payment form immediately
    const user = auth.currentUser;
    if (user) {
      try {
        const res = await fetch('/api/create-subscription-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: user.uid, priceId: plan.id })
        });

        if (res.ok) {
          const data = await res.json();
          if (data.clientSecret && data.customerId) {
            sessionStorage.setItem('checkoutPrefetch', JSON.stringify({
              clientSecret: data.clientSecret,
              customerId: data.customerId,
              paymentIntentId: data.paymentIntentId || null,
              isAuthorizationFlow: !!data.isAuthorizationFlow,
              prefetchedAt: Date.now()
            }));
          }
        }
      } catch (err) {
        console.error('Prefetch checkout failed, will retry on /checkout:', err);
      }
    }

    window.location.href = '/checkout';
  };

  // Show loading state while country is being detected
  if (!countryLoaded) {
    return (
      <div 
        className='min-h-screen text-white relative flex items-center justify-center'
        style={{
          background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'
        }}
      >
        <div className="flex items-center space-x-3">
          <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-white/80">Loading pricing...</span>
        </div>
      </div>
    );
  }

  const activePlans = (bizToggle || isBizLanding) ? BIZ_PLANS : (isTollFreeLanding || isAIPhoneLanding) ? TOLL_FREE_PLANS : PLANS;
  const currentPlan = activePlans[selectedPlan];
  // Consumer mode = the default /welcome experience (no biz toggle, no toll-free / AI / biz landing)
  const isConsumerMode = !bizToggle && !isBizLanding && !isTollFreeLanding && !isAIPhoneLanding;
  // Trial CTA copy: the consumer monthly plan now runs the authorization-hold + free-trial flow.
  // Toll-free "monthly" keeps its previous (non-trial CTA) behavior.
  const showTrialCta = !!currentPlan?.hasTrial && (selectedPlan !== 'monthly' || isConsumerMode);
  // Green CTA styling was reserved for the non-trial monthly plan; the consumer monthly is now the
  // primary (purple) plan, so green only remains for toll-free / AI landing monthly.
  const useGreenCta = selectedPlan === 'monthly' && !bizToggle && !isConsumerMode;

  return (
    <div 
      className={`min-h-screen text-white relative ${isMobile ? 'pb-20' : 'pb-8'}`}
      style={{
        background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'
      }}
    >
      <style>{`
        @keyframes weeklyGlow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(147, 51, 234, 0.3), 0 0 30px rgba(147, 51, 234, 0.1);
          }
          25% {
            box-shadow: 8px -4px 20px rgba(168, 85, 247, 0.5), -8px 4px 35px rgba(79, 70, 229, 0.2);
          }
          50% {
            box-shadow: 0 0 22px rgba(139, 92, 246, 0.6), 0 0 45px rgba(147, 51, 234, 0.15);
          }
          75% {
            box-shadow: -8px 4px 20px rgba(168, 85, 247, 0.5), 8px -4px 35px rgba(79, 70, 229, 0.2);
          }
        }
        .weekly-glow-wrapper {
          border-radius: 1.5rem;
          animation: weeklyGlow 3s ease-in-out infinite;
        }
        @keyframes badgeGlow {
          0%, 100% { box-shadow: 0 0 6px rgba(147, 51, 234, 0.3), 0 0 12px rgba(147, 51, 234, 0.1); }
          50% { box-shadow: 0 0 10px rgba(168, 85, 247, 0.6), 0 0 20px rgba(147, 51, 234, 0.25); }
        }
        .trial-badge-glow {
          animation: badgeGlow 2s ease-in-out infinite;
        }
        @keyframes testimonialFade {
          0%, 40% { opacity: 1; transform: translateY(0); }
          45%, 50% { opacity: 0; transform: translateY(-8px); }
          55%, 60% { opacity: 0; transform: translateY(8px); }
          65%, 100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes toggleGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(168, 85, 247, 0.4), 0 0 16px rgba(168, 85, 247, 0.1); }
          50% { box-shadow: 0 0 12px rgba(168, 85, 247, 0.6), 0 0 24px rgba(168, 85, 247, 0.2); }
        }
        .biz-toggle-active {
          animation: toggleGlow 2s ease-in-out infinite;
        }
      `}</style>

      {/* Discreet help button in upper left */}
      <button
        onClick={() => {
          // Update URL without reloading to trigger the help state
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('help-please', '');
          window.history.pushState({}, '', currentUrl.toString());
          
          // Use the proper helper function to show Intercom
          showIntercomForHelp();
        }}
        className="fixed top-4 left-4 sm:top-6 sm:left-6 bg-black/10 backdrop-blur-3xl rounded-xl px-3 py-2 border border-white/5 shadow-xl z-50 hover:bg-black/20 hover:border-white/10 transition-all duration-300 group"
        style={{
          background: 'rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(20px)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 16px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.02)'
        }}
        title="Get help"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40 group-hover:text-white/60 transition-colors duration-300 font-medium">Help</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/40 group-hover:text-white/60 transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </button>

      {/* User email or phone display in upper right */}
      {auth.currentUser && !isMobile && (
        <div className="fixed top-4 right-4 sm:top-6 sm:right-6 bg-black/20 backdrop-blur-3xl rounded-2xl px-4 py-3 border border-white/10 shadow-2xl z-50"
             style={{
               background: 'rgba(0, 0, 0, 0.1)',
               backdropFilter: 'blur(20px)',
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
             }}>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm text-white/80 font-medium truncate max-w-[200px]">
              {auth.currentUser.phoneNumber ? 
                formatPhoneNumber(auth.currentUser.phoneNumber) : 
                auth.currentUser.email || 'Signed In'}
            </span>
          </div>
        </div>
      )}
      
      <section className={`${isMobile ? 'pt-4 px-4' : 'pt-12 px-6'} min-h-screen flex items-center justify-center`}>
        <div className={`mx-auto w-full ${isMobile ? 'max-w-sm' : 'max-w-6xl'}`}>
          
                    {/* Header */}
          {!isMobile && (
            <div className="text-center mb-8">
              <h1 className="text-4xl font-light tracking-tight text-white/90 mb-4"
                   style={{
                    textShadow: 'inset 0 2px 4px rgba(0,0,0,0.8), 0 1px 0 rgba(255,255,255,0.1)',
                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
                  }}>
                Choose Your Plan
              </h1>
              {/* Rotating testimonial - Desktop */}
              <div className="overflow-hidden mx-auto max-w-md" style={{ minHeight: '48px' }}>
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="transition-all duration-500"
                    style={{
                      display: i === testimonialIndex ? 'block' : 'none',
                      animation: i === testimonialIndex ? 'testimonialFade 5s ease-in-out' : 'none',
                    }}
                  >
                    <p className="text-white/60 text-sm italic leading-snug">"{t.quote}"</p>
                    <p className="text-purple-300/70 text-xs mt-1 font-medium">— {t.author}, {t.location}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

                    {/* Main Content Layout */}
          <div className={`${isMobile ? 'space-y-8' : 'flex gap-12 items-start'} ${isMobile ? 'pb-32' : 'mb-8'}`}>
            
            {/* Features Section */}
            <div className={`${isMobile ? 'hidden' : 'flex-1 min-w-0'} bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6`}
                 style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4)' }}>
              <h3 className="text-xl font-light text-white mb-4 text-center">
                {(isTollFreeLanding || isAIPhoneLanding) ? "What's included" : "What's included in all plans"}
              </h3>
              <ul className="space-y-3">
                {((isTollFreeLanding || isAIPhoneLanding) ? [
                  '1 Toll Free Number or any Area Code',
                  'Phone number privacy',
                  'Call & SMS forwarding',
                  'Send & receive texts online',
                  'Message history & search',
                  '✨ AI Feature: Let your own personalized AI respond to calls and messages'
                ] : [
                  '1 Phone number (any Area Code)',
                  'Call & SMS forwarding',
                  'Send & receive texts online',
                  'Message history & search',
                  '🇺🇸 🇨🇦 & 🇬🇧 numbers',
                  '✨ AI Feature: Let your own personalized AI respond to calls and messages'
                ]).map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    {feature.startsWith('✨ AI Feature:') ? (
                      <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3 h-3 text-purple-400" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    )}
                    <span className={`text-base ${
                      feature.startsWith('✨ AI Feature:') ? 'text-purple-300 font-medium' : 'text-white/80'
                    }`}>
                      {feature.replace('✨ ', '')}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Business Features Toggle */}
              {!(isTollFreeLanding || isAIPhoneLanding) && (
                <>
                  <div className="my-4 border-t border-white/5"></div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-white/70">Business Features</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] transition-opacity duration-300 ${bizToggle ? 'opacity-0' : 'text-white/30 opacity-100'}`}>Toggle to enable</span>
                    <button
                      onClick={handleBizToggle}
                      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${bizToggle ? 'biz-toggle-active' : ''}`}
                      style={{
                        background: bizToggle
                          ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.6) 0%, rgba(79, 70, 229, 0.5) 100%)'
                          : 'rgba(255, 255, 255, 0.08)',
                        border: bizToggle ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(12px)',
                      }}
                    >
                      <div
                        className="absolute w-5 h-5 rounded-full transition-all duration-300"
                        style={{
                          top: '50%',
                          transform: 'translateY(-50%)',
                          left: bizToggle ? 'calc(100% - 1.375rem)' : '0.125rem',
                          background: bizToggle
                            ? 'linear-gradient(135deg, #fff 0%, #e9d5ff 100%)'
                            : 'rgba(255, 255, 255, 0.3)',
                          boxShadow: bizToggle
                            ? '0 2px 8px rgba(168, 85, 247, 0.4), 0 0 0 1px rgba(255,255,255,0.2)'
                            : '0 1px 4px rgba(0,0,0,0.3)',
                        }}
                      />
                    </button>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {[
                      { text: 'Get a toll-free number (800)', icon: 'phone' },
                      { text: 'Call with AI', icon: 'sparkle' },
                    ].map((feature, index) => (
                      <li key={`biz-${index}`} className={`flex items-center gap-3 transition-all duration-300 ${bizToggle ? '' : 'opacity-30'}`}>
                        {feature.icon === 'sparkle' ? (
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${bizToggle ? 'bg-purple-500/20' : 'bg-white/5'}`}>
                            <SparklesIcon className={`w-3 h-3 transition-colors duration-300 ${bizToggle ? 'text-purple-400' : 'text-white/30'}`} />
                          </div>
                        ) : (
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${bizToggle ? 'bg-purple-500/20' : 'bg-white/5'}`}>
                            <svg className={`w-3 h-3 transition-colors duration-300 ${bizToggle ? 'text-purple-400' : 'text-white/30'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                            </svg>
                          </div>
                        )}
                        <span className={`text-base transition-colors duration-300 ${bizToggle ? 'text-purple-300 font-medium' : 'text-white/30'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Plan Options */}
            <div className={`${isMobile ? 'hidden' : 'w-96 flex-shrink-0 space-y-4'}`}>
            
            {(isTollFreeLanding || isAIPhoneLanding) ? (
              // Toll-free plans for business landing
              <>
                {/* Monthly Toll-Free Plan */}
                <div 
                  className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                    selectedPlan === 'monthly' ? 'scale-105' : ''
                  }`}
                  onClick={() => handlePlanSelection('monthly')}
                >
                  {/* Trial Badge */}
                  {selectedPlan === 'monthly' && TOLL_FREE_PLANS.monthly.hasTrial && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-green-500/90 to-emerald-500/90 backdrop-blur-xl border border-green-400/30 px-4 py-1 rounded-full text-xs font-medium text-white">
                        ✨ 30 Days Free Trial
                      </div>
                    </div>
                  )}
                  <div 
                    className={`bg-black/20 backdrop-blur-3xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                      selectedPlan === 'monthly' 
                        ? 'border-green-400/50 ring-2 ring-green-400/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      background: selectedPlan === 'monthly' 
                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: selectedPlan === 'monthly'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(34, 197, 94, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    <div className={`${isMobile ? 'p-3 text-center' : 'p-6'}`}>
                      <div className={`${isMobile ? 'space-y-2' : 'flex items-start justify-between mb-3'}`}>
                        <div className={`${isMobile ? 'space-y-2' : 'flex-1'}`}>
                          <div className={`${isMobile ? 'flex flex-col items-center gap-2' : 'flex items-center gap-3 mb-2'}`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                              selectedPlan === 'monthly' 
                                ? 'border-green-400 bg-green-400' 
                                : 'border-white/30'
                            }`}>
                              {selectedPlan === 'monthly' && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-light text-white`}>Monthly</h3>
                          </div>
                          {!isMobile && <p className="text-white/60 text-sm mb-2">{TOLL_FREE_PLANS.monthly.subtitle}</p>}
                          
                          <div className={`${isMobile ? 'flex flex-col items-center gap-1' : 'flex items-baseline gap-2 mb-4'}`}>
                            <span className={`${isMobile ? 'text-2xl' : 'text-4xl'} font-light text-white`}>{TOLL_FREE_PLANS.monthly.price}</span>
                            <span className={`text-white/60 ${isMobile ? 'text-sm' : ''}`}>{TOLL_FREE_PLANS.monthly.displayPeriod}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Annual Toll-Free Plan */}
                <div 
                  className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                    selectedPlan === 'annual' ? 'scale-105' : ''
                  }`}
                  onClick={() => handlePlanSelection('annual')}
                >
                  {/* Trial Badge */}
                  {selectedPlan === 'annual' && TOLL_FREE_PLANS.annual.hasTrial && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/30 px-4 py-1 rounded-full text-xs font-medium text-white">
                        ✨ 30 Days Free Trial
                      </div>
                    </div>
                  )}
                  <div 
                    className={`bg-black/20 backdrop-blur-3xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                      selectedPlan === 'annual' 
                        ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      background: selectedPlan === 'annual' 
                        ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: selectedPlan === 'annual'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    <div className={`${isMobile ? 'p-3 text-center' : 'p-6'}`}>
                      <div className={`${isMobile ? 'space-y-2' : 'flex items-start justify-between mb-3'}`}>
                        <div className={`${isMobile ? 'space-y-2' : 'flex-1'}`}>
                          <div className={`${isMobile ? 'flex flex-col items-center gap-2' : 'flex items-center gap-3 mb-2'}`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                              selectedPlan === 'annual' 
                                ? 'border-purple-400 bg-purple-400' 
                                : 'border-white/30'
                            }`}>
                              {selectedPlan === 'annual' && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-light text-white`}>Annual</h3>
                          </div>
                          {!isMobile && <p className="text-white/60 text-sm mb-2">{TOLL_FREE_PLANS.annual.subtitle}</p>}
                          
                          <div className={`${isMobile ? 'flex flex-col items-center gap-1' : 'flex items-baseline gap-2 mb-2'}`}>
                            <span className={`${isMobile ? 'text-2xl' : 'text-4xl'} font-light text-white`}>{TOLL_FREE_PLANS.annual.price}</span>
                            <span className={`text-white/60 ${isMobile ? 'text-sm' : ''}`}>{TOLL_FREE_PLANS.annual.displayPeriod}</span>
                          </div>
                          
                          {!isMobile && (
                          <div className="mb-4">
                            <div className="inline-block bg-purple-500/20 border border-purple-400/30 text-purple-300 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                              Total {TOLL_FREE_PLANS.annual.originalPrice}
                            </div>
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : bizToggle ? (
              // Business plans (toggle ON)
              <>
                {/* Biz 3-Month Plan - Primary position with glow */}
                <div 
                  className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                    selectedPlan === 'threeMonths' ? 'scale-105' : ''
                  }`}
                  onClick={() => handlePlanSelection('threeMonths')}
                >
                  <div className="absolute -top-3 inset-x-0 flex justify-center z-10">
                    <div className="trial-badge-glow bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/40 px-4 py-1 rounded-full text-xs font-medium text-white shadow-lg shadow-purple-500/20 whitespace-nowrap">
                      ✨ 6 Days Free Trial
                    </div>
                  </div>
                  <div className="weekly-glow-wrapper">
                  <div 
                    className={`bg-black/20 backdrop-blur-3xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                      selectedPlan === 'threeMonths' 
                        ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      background: selectedPlan === 'threeMonths' 
                        ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: selectedPlan === 'threeMonths'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    <div className="p-6" style={{ minHeight: '152px' }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedPlan === 'threeMonths' 
                            ? 'border-purple-400 bg-purple-400' 
                            : 'border-white/30'
                        }`}>
                          {selectedPlan === 'threeMonths' && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <h3 className="text-2xl font-light text-white">Best Value</h3>
                      </div>
                      <div className="h-5"></div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-3xl font-light text-white">$1.99</span>
                        <span className="text-white/60">/day</span>
                      </div>
                      <p className="text-white/40 text-xs mt-2">billed every 3-months</p>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Biz Weekly Plan - Power Users */}
                <div 
                  className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                    selectedPlan === 'weekly' ? 'scale-105' : ''
                  }`}
                  onClick={() => handlePlanSelection('weekly')}
                >
                  <div 
                    className={`bg-black/20 backdrop-blur-3xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                      selectedPlan === 'weekly' 
                        ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      background: selectedPlan === 'weekly' 
                        ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: selectedPlan === 'weekly'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    <div className="p-6" style={{ minHeight: '152px' }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedPlan === 'weekly' 
                            ? 'border-purple-400 bg-purple-400' 
                            : 'border-white/30'
                        }`}>
                          {selectedPlan === 'weekly' && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <h3 className="text-2xl font-light text-white">Power Users</h3>
                      </div>
                      <div className="h-5"></div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-3xl font-light text-white">$4.28</span>
                        <span className="text-white/60">/day</span>
                      </div>
                      <p className="text-white/50 text-sm mt-1">billed as $29.99/week</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Regular plans — weekly is hidden; monthly is the single primary plan
              <>
                {/* Monthly Plan (primary, 3-day free trial via authorization hold) */}
                <div 
                  className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                    selectedPlan === 'monthly' ? 'scale-105' : ''
                  }`}
                  onClick={() => handlePlanSelection('monthly')}
                >
                  {/* Trial Badge - Always visible */}
                  {PLANS.monthly.hasTrial && (
                    <div className="absolute -top-3 inset-x-0 flex justify-center z-10">
                      <div className="trial-badge-glow bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/40 px-4 py-1 rounded-full text-xs font-medium text-white shadow-lg shadow-purple-500/20 whitespace-nowrap">
                        ✨ {PLANS.monthly.trialDays} Days Free Trial
                      </div>
                    </div>
                  )}
                  <div className="weekly-glow-wrapper">
                  <div 
                    className={`bg-black/20 backdrop-blur-3xl rounded-3xl border transition-all duration-300 overflow-hidden ${
                      selectedPlan === 'monthly' 
                        ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      background: selectedPlan === 'monthly' 
                        ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                        : 'rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: selectedPlan === 'monthly'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                    }}
                  >
                    <div className="p-6" style={{ minHeight: '152px' }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          selectedPlan === 'monthly' 
                            ? 'border-purple-400 bg-purple-400' 
                            : 'border-white/30'
                        }`}>
                          {selectedPlan === 'monthly' && (
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          )}
                        </div>
                        <h3 className="text-2xl font-light text-white">Most Popular</h3>
                      </div>
                      <div className="h-5"></div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-3xl font-light text-white">Only $0.67</span>
                        <span className="text-white/60">/day</span>
                      </div>
                      <p className="text-white/50 text-sm mt-1">billed as {PLANS.monthly.price}/month</p>
                    </div>
                  </div>
                  </div>
                </div>
              </>
            )}
            
            </div>
          </div>

                    {/* CTA Button - Desktop Only */}
          {!isMobile && (
            <div>
              <button
                onClick={handleCheckout}
                disabled={isLoading}
                className={`w-full py-4 px-6 rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 ${
                  useGreenCta
                    ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 hover:from-green-500/40 hover:to-emerald-500/40 text-green-300 border border-green-400/30 hover:border-green-400/50' 
                    : 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 hover:from-purple-500/40 hover:to-blue-500/40 text-white border border-purple-400/30 hover:border-purple-400/50'
                }`}
                style={{ 
                  backdropFilter: 'blur(20px)',
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(${useGreenCta ? '34, 197, 94' : '147, 51, 234'}, 0.3), 0 0 0 1px rgba(255,255,255,0.05)` 
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <span>Starting...</span>
                    <svg className="animate-spin ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <span className="text-lg">
                    {showTrialCta ? 'Start Free Trial' : 'Get Started'}
                  </span>
                )}
              </button>
              
              {showTrialCta && !isLoading && (
                <p className="text-xs text-white/50 mt-3 text-center">Cancel anytime during {currentPlan.trialDays}-day trial</p>
              )}
            </div>
          )}
        </div>
      </section>
      
             {/* Mobile Sticky Plans and Button */}
       {isMobile && (
         <div className="fixed bottom-0 left-0 right-0 z-40">
           {/* Sticky Plans Section */}
           <div className="bg-black/30 backdrop-blur-2xl border-t border-white/10 p-4"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 -8px 32px rgba(0,0,0,0.4)'
              }}>
             
             {/* Rotating testimonial */}
             <div className="mb-3 px-2 overflow-hidden" style={{ minHeight: '52px' }}>
               {testimonials.map((t, i) => (
                 <div
                   key={i}
                   className="text-center transition-all duration-500"
                   style={{
                     display: i === testimonialIndex ? 'block' : 'none',
                     animation: i === testimonialIndex ? 'testimonialFade 5s ease-in-out' : 'none',
                   }}
                 >
                   <p className="text-white/70 text-sm italic leading-snug">"{t.quote}"</p>
                   <p className="text-purple-300/80 text-xs mt-1 font-medium">— {t.author}, {t.location}</p>
                 </div>
               ))}
             </div>

             {/* What's included section for mobile */}
             <div className="mb-4 bg-black/20 backdrop-blur-2xl rounded-xl border border-white/10 p-3"
                  style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)' }}>
               <ul className="space-y-2">
                 {((isTollFreeLanding || isAIPhoneLanding) ? [
                   '1 Toll Free Number or any Area Code',
                   'Phone number privacy',
                   'Call & SMS forwarding',
                   'Send & receive texts online',
                   'Message history & search',
                   '✨ AI Feature: Let your own personalized AI respond to calls and messages'
                 ] : [
                   '1 Phone number (any Area Code)',
                   'Call & SMS forwarding',
                   'Send & receive texts online',
                   'Message history & search',
                   '🇺🇸 🇨🇦 & 🇬🇧 numbers',
                   '✨ AI Feature: Let your own personalized AI respond to calls and messages'
                 ]).map((feature, index) => (
                   <li key={index} className="flex items-center gap-3">
                     {feature.startsWith('✨ AI Feature:') ? (
                       <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                         <SparklesIcon className="w-3 h-3 text-purple-400" />
                       </div>
                     ) : (
                       <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                         <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                         </svg>
                       </div>
                     )}
                     <span className={`text-base ${
                       feature.startsWith('✨ AI Feature:') ? 'text-purple-300 font-medium' : 'text-white/80'
                     }`}>
                       {feature.replace('✨ ', '')}
                     </span>
                   </li>
                 ))}
               </ul>

               {/* Business Features Toggle - Mobile */}
               {!(isTollFreeLanding || isAIPhoneLanding) && (
                 <>
                   <div className="my-3 border-t border-white/5"></div>
                   <div className="flex items-center justify-between mb-2">
                     <span className="text-sm font-medium text-white/70">Business Features</span>
                     <div className="flex items-center gap-2">
                       <span className={`text-[10px] transition-opacity duration-300 ${bizToggle ? 'opacity-0' : 'text-white/30 opacity-100'}`}>Toggle to enable</span>
                     <button
                       onClick={handleBizToggle}
                       className={`relative w-12 h-6 rounded-full transition-all duration-300 ${bizToggle ? 'biz-toggle-active' : ''}`}
                       style={{
                         background: bizToggle
                           ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.6) 0%, rgba(79, 70, 229, 0.5) 100%)'
                           : 'rgba(255, 255, 255, 0.08)',
                         border: bizToggle ? '1px solid rgba(168, 85, 247, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                         backdropFilter: 'blur(12px)',
                       }}
                     >
                       <div
                         className="absolute w-5 h-5 rounded-full transition-all duration-300"
                         style={{
                           top: '50%',
                           transform: 'translateY(-50%)',
                           left: bizToggle ? 'calc(100% - 1.375rem)' : '0.125rem',
                           background: bizToggle
                             ? 'linear-gradient(135deg, #fff 0%, #e9d5ff 100%)'
                             : 'rgba(255, 255, 255, 0.3)',
                           boxShadow: bizToggle
                             ? '0 2px 8px rgba(168, 85, 247, 0.4), 0 0 0 1px rgba(255,255,255,0.2)'
                             : '0 1px 4px rgba(0,0,0,0.3)',
                         }}
                       />
                     </button>
                     </div>
                   </div>
                   <ul className="space-y-2">
                     {[
                       { text: 'Get a toll-free number (800)', icon: 'phone' },
                       { text: 'Call with AI', icon: 'sparkle' },
                     ].map((feature, index) => (
                       <li key={`biz-m-${index}`} className={`flex items-center gap-3 transition-all duration-300 ${bizToggle ? '' : 'opacity-30'}`}>
                         {feature.icon === 'sparkle' ? (
                           <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${bizToggle ? 'bg-purple-500/20' : 'bg-white/5'}`}>
                             <SparklesIcon className={`w-3 h-3 transition-colors duration-300 ${bizToggle ? 'text-purple-400' : 'text-white/30'}`} />
                           </div>
                         ) : (
                           <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${bizToggle ? 'bg-purple-500/20' : 'bg-white/5'}`}>
                             <svg className={`w-3 h-3 transition-colors duration-300 ${bizToggle ? 'text-purple-400' : 'text-white/30'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                             </svg>
                           </div>
                         )}
                         <span className={`text-base transition-colors duration-300 ${bizToggle ? 'text-purple-300 font-medium' : 'text-white/30'}`}>
                           {feature.text}
                         </span>
                       </li>
                     ))}
                   </ul>
                 </>
               )}
             </div>
             
             <div className={`grid ${isConsumerMode ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-4`}>
               {(isTollFreeLanding || isAIPhoneLanding) ? (
                 // Toll-free plans for business landing
                 <>
                   {/* Monthly Toll-Free Plan - Sticky */}
                   <div 
                     className={`relative cursor-pointer transition-all duration-300 ${
                       selectedPlan === 'monthly' ? 'scale-105' : ''
                     }`}
                     onClick={() => handlePlanSelection('monthly')}
                   >
                     {/* Trial Badge - Only when selected */}
                     {selectedPlan === 'monthly' && TOLL_FREE_PLANS.monthly.hasTrial && (
                       <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                         <div className="bg-gradient-to-r from-green-500/90 to-emerald-500/90 backdrop-blur-xl border border-green-400/30 px-2 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap">
                           ✨ 30 Days Free
                         </div>
                       </div>
                     )}
                     
                     {/* Selection Dot - Upper Right */}
                     <div className="absolute top-2 right-2 z-10">
                       <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                         selectedPlan === 'monthly' 
                           ? 'border-green-400 bg-green-400' 
                           : 'border-white/30'
                       }`}>
                         {selectedPlan === 'monthly' && (
                           <div className="w-2 h-2 rounded-full bg-white"></div>
                         )}
                       </div>
                     </div>
                     
                     <div 
                       className={`bg-black/20 backdrop-blur-3xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                         selectedPlan === 'monthly' 
                           ? 'border-green-400/50 ring-2 ring-green-400/30' 
                           : 'border-white/10 hover:border-white/20'
                       }`}
                       style={{
                         background: selectedPlan === 'monthly' 
                           ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                           : 'rgba(0, 0, 0, 0.1)',
                         backdropFilter: 'blur(20px)',
                         boxShadow: selectedPlan === 'monthly'
                           ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(34, 197, 94, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                           : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                       }}
                     >
                       <div className="p-3 text-center">
                         <div className="space-y-2">
                           <h3 className="text-lg font-light text-white mt-2">Monthly</h3>
                           <div className="flex flex-col items-center gap-1">
                             <span className="text-2xl font-light text-white">{TOLL_FREE_PLANS.monthly.price}</span>
                             <span className="text-white/60 text-sm">/month</span>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Annual Toll-Free Plan - Sticky */}
                   <div 
                     className={`relative cursor-pointer transition-all duration-300 ${
                       selectedPlan === 'annual' ? 'scale-105' : ''
                     }`}
                     onClick={() => handlePlanSelection('annual')}
                   >
                     {/* Trial Badge - Only when selected */}
                     {selectedPlan === 'annual' && TOLL_FREE_PLANS.annual.hasTrial && (
                       <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                         <div className="bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/30 px-2 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap">
                           ✨ 30 Days Free
                         </div>
                       </div>
                     )}
                     
                     {/* Selection Dot - Upper Right */}
                     <div className="absolute top-2 right-2 z-10">
                       <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                         selectedPlan === 'annual' 
                           ? 'border-purple-400 bg-purple-400' 
                           : 'border-white/30'
                       }`}>
                         {selectedPlan === 'annual' && (
                           <div className="w-2 h-2 rounded-full bg-white"></div>
                         )}
                       </div>
                     </div>
                     
                     <div 
                       className={`bg-black/20 backdrop-blur-3xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                         selectedPlan === 'annual' 
                           ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                           : 'border-white/10 hover:border-white/20'
                       }`}
                       style={{
                         background: selectedPlan === 'annual' 
                           ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                           : 'rgba(0, 0, 0, 0.1)',
                         backdropFilter: 'blur(20px)',
                         boxShadow: selectedPlan === 'annual'
                           ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                           : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                       }}
                     >
                       <div className="p-3 text-center">
                         <div className="space-y-2">
                           <h3 className="text-lg font-light text-white mt-2">Annual</h3>
                           <div className="flex flex-col items-center gap-1">
                             <span className="text-2xl font-light text-white">{TOLL_FREE_PLANS.annual.price}</span>
                             <span className="text-white/60 text-sm">/mo paid yearly</span>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </>
               ) : bizToggle ? (
                 // Business plans - Mobile (toggle ON)
                 <>
                   {/* Biz 3-Month Plan - Primary position with glow */}
                   <div 
                     className={`relative cursor-pointer transition-all duration-300 ${
                       selectedPlan === 'threeMonths' ? 'scale-105' : ''
                     }`}
                     onClick={() => handlePlanSelection('threeMonths')}
                   >
                     <div className="absolute -top-3 inset-x-0 flex justify-center z-20">
                       <div className="trial-badge-glow bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/40 px-2 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap shadow-lg shadow-purple-500/20">
                         ✨ 6 Days Free Trial
                       </div>
                     </div>
                     
                     <div className="absolute top-2 right-2 z-10">
                       <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                         selectedPlan === 'threeMonths' 
                           ? 'border-purple-400 bg-purple-400' 
                           : 'border-white/30'
                       }`}>
                         {selectedPlan === 'threeMonths' && (
                           <div className="w-2 h-2 rounded-full bg-white"></div>
                         )}
                       </div>
                     </div>
                     
                     <div className="weekly-glow-wrapper" style={{ borderRadius: '1rem' }}>
                     <div 
                       className={`bg-black/20 backdrop-blur-3xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                         selectedPlan === 'threeMonths' 
                           ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                           : 'border-white/10 hover:border-white/20'
                       }`}
                       style={{
                         background: selectedPlan === 'threeMonths' 
                           ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                           : 'rgba(0, 0, 0, 0.1)',
                         backdropFilter: 'blur(20px)',
                         boxShadow: selectedPlan === 'threeMonths'
                           ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                           : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                       }}
                     >
                       <div className="p-3 pt-4 text-center" style={{ minHeight: '110px' }}>
                          <h3 className="text-base font-medium text-purple-300">Best Value</h3>
                          <span className="text-lg font-light text-white mt-1 block">$1.99<span className="text-white/60 text-sm">/day</span></span>
                          <span className="text-white/50 text-xs block mt-0.5">billed every 3-months</span>
                       </div>
                     </div>
                     </div>
                   </div>

                   {/* Biz Weekly Plan - Power Users */}
                   <div 
                     className={`relative cursor-pointer transition-all duration-300 ${
                       selectedPlan === 'weekly' ? 'scale-105' : ''
                     }`}
                     onClick={() => handlePlanSelection('weekly')}
                   >
                     <div className="absolute top-2 right-2 z-10">
                       <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                         selectedPlan === 'weekly' 
                           ? 'border-purple-400 bg-purple-400' 
                           : 'border-white/30'
                       }`}>
                         {selectedPlan === 'weekly' && (
                           <div className="w-2 h-2 rounded-full bg-white"></div>
                         )}
                       </div>
                     </div>
                     
                     <div 
                       className={`bg-black/20 backdrop-blur-3xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                         selectedPlan === 'weekly' 
                           ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                           : 'border-white/10 hover:border-white/20'
                       }`}
                       style={{
                         background: selectedPlan === 'weekly' 
                           ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                           : 'rgba(0, 0, 0, 0.1)',
                         backdropFilter: 'blur(20px)',
                         boxShadow: selectedPlan === 'weekly'
                           ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                           : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                       }}
                     >
                       <div className="p-3 pt-4 text-center" style={{ minHeight: '110px' }}>
                          <h3 className="text-base font-medium text-purple-300">Power Users</h3>
                          <span className="text-lg font-light text-white mt-1 block">$4.28<span className="text-white/60 text-sm">/day</span></span>
                          <span className="text-white/50 text-xs block mt-0.5">billed as $29.99/wk</span>
                       </div>
                     </div>
                   </div>
                 </>
               ) : (
                 // Regular plans — weekly is hidden; monthly is the single primary plan
                 <>
                   {/* Monthly Plan - Sticky (primary, 3-day free trial via authorization hold) */}
                   <div 
                     className={`relative cursor-pointer transition-all duration-300 ${
                       selectedPlan === 'monthly' ? 'scale-[1.02]' : ''
                     }`}
                     onClick={() => handlePlanSelection('monthly')}
                   >
                     {/* Trial Badge - Always visible */}
                     {PLANS.monthly.hasTrial && (
                       <div className="absolute -top-3 inset-x-0 flex justify-center z-20">
                         <div className="trial-badge-glow bg-gradient-to-r from-purple-500/90 to-blue-500/90 backdrop-blur-xl border border-purple-400/40 px-3 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap shadow-lg shadow-purple-500/20">
                           ✨ {PLANS.monthly.trialDays} Days Free Trial
                         </div>
                       </div>
                     )}
                     
                     {/* Selection Dot - Upper Right */}
                     <div className="absolute top-2 right-2 z-10">
                       <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                         selectedPlan === 'monthly' 
                           ? 'border-purple-400 bg-purple-400' 
                           : 'border-white/30'
                       }`}>
                         {selectedPlan === 'monthly' && (
                           <div className="w-2 h-2 rounded-full bg-white"></div>
                         )}
                       </div>
                     </div>
                     
                     <div className="weekly-glow-wrapper" style={{ borderRadius: '1rem' }}>
                     <div 
                       className={`bg-black/20 backdrop-blur-3xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                         selectedPlan === 'monthly' 
                           ? 'border-purple-400/50 ring-2 ring-purple-400/30' 
                           : 'border-white/10 hover:border-white/20'
                       }`}
                       style={{
                         background: selectedPlan === 'monthly' 
                           ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)'
                           : 'rgba(0, 0, 0, 0.1)',
                         backdropFilter: 'blur(20px)',
                         boxShadow: selectedPlan === 'monthly'
                           ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 80px rgba(147, 51, 234, 0.2), 0 0 0 1px rgba(255,255,255,0.05)'
                           : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                       }}
                     >
                       <div className="p-3 pt-4 text-center" style={{ minHeight: '96px' }}>
                          <h3 className="text-base font-medium text-purple-300">Most Popular</h3>
                          <span className="text-lg font-light text-white mt-1 block">Only $0.67<span className="text-white/60 text-sm">/day</span></span>
                          <span className="text-white/50 text-xs block mt-0.5">billed as {PLANS.monthly.price}/month · cancel anytime</span>
                       </div>
                     </div>
                     </div>
                   </div>
                 </>
               )}
             </div>
           </div>
           
           {/* Sticky Button */}
           <div className="bg-black/30 backdrop-blur-2xl border-t border-white/10 p-4"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 -8px 32px rgba(0,0,0,0.4)'
                }}>
                           <button 
                onClick={handleCheckout}
                disabled={isLoading}
                className={`w-full py-4 px-6 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                  useGreenCta
                    ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 hover:from-green-500/40 hover:to-emerald-500/40 text-green-300 border border-green-400/30 hover:border-green-400/50' 
                    : 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 hover:from-purple-500/40 hover:to-blue-500/40 text-white border border-purple-400/30 hover:border-purple-400/50'
                }`}
                style={{ 
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(${useGreenCta ? '34, 197, 94' : '147, 51, 234'}, 0.3)` 
                }}
             >
               {isLoading ? (
                 <div className="flex items-center justify-center">
                   <span>Starting...</span>
                   <svg className="animate-spin ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                 </div>
               ) : (
                 <span>
                   {showTrialCta ? 'Start Free Trial' : 'Get Started'}
                 </span>
               )}
             </button>
           </div>
         </div>
       )}

       {/* Desktop Sticky Bottom Bar */}
       {!isMobile && (
         <div className="fixed bottom-0 left-0 right-0 bg-black/30 backdrop-blur-2xl border-t border-white/10 shadow-2xl z-40 py-4 px-4 md:px-8"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 -8px 32px rgba(0,0,0,0.4)'
              }}>
             <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
               {/* Plan summary */}
               <div className="flex items-center space-x-6">
                 <div>
                   <div className="flex items-center">
                     <span className="text-white/70 mr-2">{currentPlan?.name}:</span>
                     {showTrialCta ? (
                       <>
                         <span className="line-through text-white/50 mr-2">{currentPlan.price}</span>
                         <span className="text-green-400 font-medium">$0</span>
                         <span className="text-white/60 ml-1">{currentPlan.displayPeriod || '/mo'}</span>
                       </>
                     ) : (
                       <>
                         <span className="text-white font-medium">{currentPlan?.price}</span>
                         <span className="text-white/60">{currentPlan?.displayPeriod}</span>
                       </>
                     )}
                   </div>
                   {showTrialCta && (
                     <span className="text-xs text-green-400">{currentPlan.trialDays}-day free trial</span>
                   )}
                 </div>
               </div>
               
               {/* CTA Button */}
                                <button 
                   onClick={handleCheckout}
                   disabled={isLoading}
                   className={`w-full sm:w-auto px-8 py-4 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                     useGreenCta
                       ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 hover:from-green-500/40 hover:to-emerald-500/40 text-green-300 border border-green-400/30 hover:border-green-400/50' 
                       : 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 hover:from-purple-500/40 hover:to-blue-500/40 text-white border border-purple-400/30 hover:border-purple-400/50'
                   }`}
                   style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(${useGreenCta ? '34, 197, 94' : '147, 51, 234'}, 0.3)` }}
               >
                 {showTrialCta ? 'Start Free Trial' : 'Get Started'}
               </button>
             </div>
         </div>
       )}

       {/* DEBUG: RevenueCat Debug Info - Remove in production */}
       {process.env.NODE_ENV === 'development' && (
         <div className="max-w-4xl mx-auto mt-8 p-4 space-y-4">
           <RevenueCatDebug />
           <RevenueCatCustomerLink />
         </div>
       )}
    </div>
  );
}
